/**
 * Everything a finished packet produces, in one place.
 *
 * The point of the redesign, distilled: a utility technician should never have to know that "the
 * PDF", "the CSVs" and "the technical manifest" are separate things, or which CSV corresponds to
 * which internal table. One action produced all of it; this shows all of it, ranked by what a human
 * actually opens.
 *
 * Deliberately NOT split into "internal" vs "auditor" sets. Every format here is the same
 * underlying sealed evidence, not different content for different audiences — splitting by audience
 * would reintroduce exactly the decision burden this redesign removes.
 *
 * ── THE PERMANENT MODEL — do not invert this ─────────────────────────────────────────────────
 * `evidence_class` is the ONLY thing that can authorize a download. Every other signal —
 * `sealed`, `sealed_bundle_integrity_hash`, a bundle id existing, a job reporting COMPLETED — may
 * only ever FURTHER RESTRICT. None of them may independently authorize, and an absent or
 * unverified signal must never be read as permission.
 *
 * This is not a stylistic preference; it is forced by real data. Bundle 3dc486f6 on dev carries
 * `evidence_class: null` alongside a PRESENT `sealed_bundle_integrity_hash` — pre-D1 records were
 * sealed before the class existed and migration b7e2d9a41c65 deliberately left them NULL. Anything
 * that treats a seal signal as sufficient will offer that record as evidence. The inverse mistake
 * has also already happened once here: an UNVERIFIED seal defaulted to "sealed" in My Packets and
 * offered downloads during the loading window before anything had been checked.
 *
 * Both directions are pinned by tests below: class without seal offers nothing, seal without class
 * offers nothing. If a future change makes either pass, the change is wrong.
 *
 * ── The seal gate, and exactly what it is ────────────────────────────────────────────────────
 * Downloads appear only once a packet is genuinely sealed: SEALED_EVIDENCE outright, or
 * PARTIAL_REVIEW after authorization. A download only means something if it is backed by the
 * sealed, tamper-evident record; anything pulled before sealing cannot be verified later and could
 * be mistaken for real evidence if it left the building.
 *
 * THIS IS PRESENTATION ONLY. Verified against the live backend 2026-08-14:
 * GET /ce/bundles/{id}/export/pdf and BOTH CSV scopes return 200 for an unsealed bundle
 * (bundle_snapshot IS NULL) — relabelled "legacy", not refused — and
 * ce_csv_export._verify_sealed_artifact returns False without ever raising, so it LABELS rather
 * than GATES. The export routes stay open and reachable by anyone with the bundle id. Hiding the
 * affordance is worth doing, but it is not the guarantee, and it must not be described as one.
 * Closing it for real needs a `bundle_snapshot IS NULL -> 409` branch in the router, which is a
 * tracked backend unit.
 */
import React, { useEffect, useState } from 'react';
import api, { errorDetail } from '../services/api';

const CSVS = [
  { key: 'observed-events', label: 'Observed events' },
  { key: 'derived-events', label: 'Derived events' },
  { key: 'command-records', label: 'Command records' },
  // S16b G6e T6 (plan docs/plans/2026-09-23-s16b-g6e-packet.md, R9): offered ONLY when
  // GET /ce/bundles/{id}/determinations answered, successfully, `present: true`. See
  // `determinationsOffered` below — a failed or unfinished read never offers it.
  { key: 'determinations', label: 'Determinations', gatedOnDeterminations: true },
];

const DETERMINATIONS_READ_FAILED = "This packet's determinations could not be read.";

/**
 * S16b G6e T6. One read of GET /ce/bundles/{bundleId}/determinations — the packet's determinations as the
 * server read them from the stored, verified bytes (plan T4). The state is one of:
 *
 *   { status: 'idle' }                 not asked (disabled, or no bundle id)
 *   { status: 'loading' }              asked, not answered
 *   { status: 'ok', data }             answered with a well-formed body (`present` a boolean,
 *                                      `section_lines` an array, non-empty when `present`)
 *   { status: 'error', error }         the request failed OR the body was malformed
 *
 * THE LOAD-BEARING DISTINCTION (plan T5 amendment (iii)): a failed read is `error`, never `ok` with
 * `present: false`. `present: false` means "this packet was sealed without determinations" and hides the
 * section silently; a failed read must not be able to produce that same silence.
 */
export function usePacketDeterminations(bundleId, enabled = true) {
  const active = Boolean(enabled && bundleId);
  const [state, setState] = useState(active ? { status: 'loading' } : { status: 'idle' });

  useEffect(() => {
    if (!active) {
      setState({ status: 'idle' });
      return undefined;
    }
    let live = true;
    setState({ status: 'loading' });
    (async () => {
      try {
        const res = await api.get(`/ce/bundles/${bundleId}/determinations`);
        const data = res && res.data;
        if (!data || typeof data.present !== 'boolean' || !Array.isArray(data.section_lines)) {
          throw new Error('malformed determinations response');
        }
        // The server yields at least one line for every packet carrying determinations
        // (ce_packet_determinations.section_lines); `present: true` with none would render a
        // heading over nothing -- read as a failure, not as an empty section.
        if (data.present && data.section_lines.length === 0) {
          throw new Error('present determinations with no section lines');
        }
        if (live) setState({ status: 'ok', data });
      } catch (e) {
        if (live) setState({ status: 'error', error: errorDetail(e, DETERMINATIONS_READ_FAILED) });
      }
    })();
    return () => { live = false; };
  }, [bundleId, active]);

  return state;
}

// The determinations CSV is offered only after a SUCCESSFUL read that says `present: true`.
function determinationsOffered(state) {
  return Boolean(state && state.status === 'ok' && state.data.present === true);
}

function saveBlob(data, filename, type) {
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function PacketDownloads({ bundleId, evidenceClass, sealed, determinations }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  // Only the two real completeness states are evidence. MANUAL_ASSEMBLY has no generation run
  // behind it and null is pre-D1 or unsealed — is_evidentiary_class() is False for both, and
  // neither may be offered as downloadable evidence regardless of what the seal flag says.
  // Gating on `sealed` alone left this class-blind; the test that caught it had been passing for
  // the wrong reason because it only ever exercised the unsealed path.
  const isEvidentiary = evidenceClass === 'SEALED_EVIDENCE' || evidenceClass === 'PARTIAL_REVIEW';

  // S16b G6e T6. A caller that already read the determinations (the generator page, which renders the
  // section from the same read) passes its state; any other caller (My Packets) gets this component's own
  // read, made only for a packet that is evidentiary and sealed — the only case that offers files at all.
  const ownDeterminations = usePacketDeterminations(
    bundleId, determinations === undefined && isEvidentiary && Boolean(sealed),
  );
  const detState = determinations === undefined ? ownDeterminations : determinations;

  if (!isEvidentiary) return null;

  // Nothing is downloadable before the seal, and the reason is stated rather than left as an
  // unexplained absence — a screen with no buttons and no explanation reads as broken.
  if (!sealed) {
    return (
      <div
        data-testid="packet-downloads-pending"
        className="bg-gray-800 rounded-xl border border-gray-700 p-5 text-sm text-gray-400"
      >
        Files become available once this packet is sealed. A packet awaiting review has not been
        sealed yet, so there is nothing here that could be verified later.
      </div>
    );
  }

  const bounded = evidenceClass === 'PARTIAL_REVIEW';

  async function download(kind, url, filename, type, responseType) {
    setBusy(kind);
    setErr(null);
    try {
      const { data } = await api.get(url, responseType ? { responseType } : undefined);
      saveBlob(responseType === 'blob' ? data : JSON.stringify(data, null, 2), filename, type);
    } catch (e) {
      setErr(errorDetail(e, 'Download failed.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div data-testid="packet-downloads" className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-white">Files</h2>
        <p className="text-xs text-gray-400 mt-1">
          {bounded
            ? 'Everything this packet produced. It is a bounded review set — not a complete packet — and every file says so.'
            : 'Everything this packet produced.'}
        </p>
      </div>

      {/* Primary: what a human opens, and what most auditors will want. */}
      <button
        type="button"
        data-download="pdf"
        disabled={busy !== null}
        onClick={() => download('pdf', `/ce/bundles/${bundleId}/export/pdf`,
          `evidence-packet-${bundleId}.pdf`, 'application/pdf', 'blob')}
        className="w-full text-left px-5 py-4 rounded-lg bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-white transition-colors"
      >
        <div className="text-sm font-semibold">{busy === 'pdf' ? 'Preparing…' : 'Full Report (PDF)'}</div>
        <div className="text-xs text-purple-200 mt-0.5">
          The complete compliance document, ready to hand to a reviewer.
        </div>
      </button>

      {/* Secondary: labelled by contents, never by internal table names. */}
      <div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
          Detailed Data (CSV)
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          {CSVS.filter((c) => !c.gatedOnDeterminations || determinationsOffered(detState)).map((c) => (
            <button
              key={c.key}
              type="button"
              data-download={`csv-${c.key}`}
              disabled={busy !== null}
              onClick={() => download(`csv-${c.key}`,
                `/ce/bundles/${bundleId}/export/csv/${c.key}`,
                `${c.key}-${bundleId}.csv`, 'text/csv', 'blob')}
              className="px-3 py-2 text-xs rounded-lg bg-blue-800 hover:bg-blue-700 disabled:opacity-50 text-blue-100 transition-colors"
            >
              {busy === `csv-${c.key}` ? 'Preparing…' : c.label}
            </button>
          ))}
        </div>
        {/* Why the determinations file is absent, when the absence is not "none was sealed": a read still
            running, or a read that failed. Never silent — a failed read must not look like `present: false`. */}
        {detState.status === 'loading' && (
          <p data-testid="determinations-download-checking" className="text-xs text-gray-500 mt-2">
            Checking for determinations…
          </p>
        )}
        {detState.status === 'error' && (
          <p data-testid="determinations-download-error" className="text-xs text-red-300 mt-2">
            {detState.error} The determinations file is not offered.
          </p>
        )}
      </div>

      {/* Tertiary: for the rare case someone needs the raw structured record. */}
      <div>
        <button
          type="button"
          data-download="json"
          disabled={busy !== null}
          onClick={() => download('json', `/ce/bundles/${bundleId}/manifest`,
            `technical-manifest-${bundleId}.json`, 'application/json')}
          className="px-3 py-1.5 text-xs rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
        >
          {busy === 'json' ? 'Preparing…' : 'Technical Manifest (JSON)'}
        </button>
      </div>

      {err && (
        <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
    </div>
  );
}

export default PacketDownloads;
